/**
 * PersistencyDashboard — Persistency AI list view.
 *
 * Shows high-risk policies sorted by lapseRiskScore. Drill-in opens a
 * detail panel with the factors and a one-click "approve re-engagement"
 * button. Empty/loading states match the AgedLeads pattern.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import { AlertTriangle, RefreshCw, MessageSquare, Calendar, X, Clock, ChevronRight, Shield } from 'lucide-react';
import toast from 'react-hot-toast';

function RiskBadge({ score }) {
  if (score >= 85) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-500/20 text-red-400 border border-red-500/30">
        <AlertTriangle className="w-3 h-3" /> {score} · critical
      </span>
    );
  }
  if (score >= 70) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-orange-500/20 text-orange-400 border border-orange-500/30">
        <AlertTriangle className="w-3 h-3" /> {score} · high
      </span>
    );
  }
  if (score >= 50) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-300 border border-amber-500/30">
        {score} · watch
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
      {score} · low
    </span>
  );
}

function ActionLabel({ a }) {
  const map = {
    no_action: 'No action',
    sms_check_in: 'SMS check-in',
    call_now: 'Call now',
    schedule_followup_in_7d: 'Schedule callback 7d',
  };
  return <span className="text-xs text-white/70">{map[a] || a}</span>;
}

function PolicyRow({ p, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(p._id)}
      className="panel p-4 w-full text-left flex items-center justify-between gap-4 hover:border-civix-purple/40 transition group"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <RiskBadge score={p.lapseRiskScore} />
          <span className="font-display font-semibold text-sm">{p.carrier || 'Unknown carrier'}</span>
          <span className="text-xs text-white/40">· {p.productType}</span>
          {p.policyNumber && <span className="text-xs text-white/40">· #{p.policyNumber}</span>}
        </div>
        <div className="mt-1 text-xs text-white/60 truncate">
          {(p.lapseRiskFactors || []).slice(0, 2).join(' · ') || 'No risk factors yet'}
        </div>
        <div className="mt-1 flex items-center gap-3 text-xs text-white/50">
          <span>Premium ${p.monthlyPremium}/mo</span>
          <span>Face ${Number(p.faceAmount || 0).toLocaleString()}</span>
          <ActionLabel a={p.lapseRiskRecommendedAction} />
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-white/60" />
    </button>
  );
}

function DetailPanel({ policyId, onClose, onAction }) {
  const { data, isLoading } = useQuery({
    queryKey: ['persistency-policy', policyId],
    queryFn: () => apiGet(`/persistency/policies/${policyId}`).then((r) => r.data),
    enabled: !!policyId,
  });

  if (!policyId) return null;
  const p = data?.policy;
  return (
    <div className="fixed inset-y-0 right-0 w-full md:w-[480px] panel z-40 overflow-y-auto p-6 border-l border-white/10">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-display font-semibold">Lapse-risk detail</h3>
        <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-sm">
          <X className="w-4 h-4" />
        </button>
      </div>
      {isLoading && <div className="text-white/60 text-sm">Loading…</div>}
      {p && (
        <>
          <div className="space-y-1">
            <RiskBadge score={p.lapseRiskScore} />
            <div className="text-sm font-semibold mt-2">{p.carrier} — {p.productType}</div>
            <div className="text-xs text-white/60">Policy #{p.policyNumber}</div>
          </div>

          <div className="mt-5">
            <div className="text-xs uppercase tracking-wide text-white/40 mb-1">Risk factors</div>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              {(p.lapseRiskFactors || []).map((f, i) => (
                <li key={i}>{f}</li>
              ))}
              {!p.lapseRiskFactors?.length && <li className="text-white/40">No factors yet</li>}
            </ul>
          </div>

          <div className="mt-5">
            <div className="text-xs uppercase tracking-wide text-white/40 mb-1">Customer</div>
            {p.lead ? (
              <div className="text-sm text-white/80">
                {p.lead.firstName} {p.lead.lastName}
                <div className="text-xs text-white/50">{p.lead.phone}</div>
                {(p.lead.optOut || p.lead.doNotContact || p.lead.dnc) && (
                  <div className="mt-1 text-xs text-red-400 inline-flex items-center gap-1">
                    <Shield className="w-3 h-3" /> DNC / opt-out on file — re-engagement will skip
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-white/50">No linked lead</div>
            )}
          </div>

          <div className="mt-5">
            <div className="text-xs uppercase tracking-wide text-white/40 mb-1">Suggested action</div>
            <div className="text-sm text-white/80"><ActionLabel a={p.lapseRiskRecommendedAction} /></div>
          </div>

          <div className="mt-6 flex flex-col gap-2">
            <button
              className="btn-primary w-full inline-flex items-center justify-center gap-2"
              onClick={() => onAction(p._id, 'approve_sms')}
            >
              <MessageSquare className="w-4 h-4" /> Approve SMS check-in
            </button>
            <button
              className="btn-secondary w-full inline-flex items-center justify-center gap-2"
              onClick={() => onAction(p._id, 'approve_callback')}
            >
              <Calendar className="w-4 h-4" /> Schedule callback in 7d
            </button>
            <button
              className="btn-secondary w-full inline-flex items-center justify-center gap-2"
              onClick={() => onAction(p._id, 'snooze_7d')}
            >
              <Clock className="w-4 h-4" /> Snooze 7d
            </button>
            <button
              className="text-xs text-white/50 hover:text-white/80 underline mt-2"
              onClick={() => onAction(p._id, 'dismiss')}
            >
              Dismiss
            </button>
          </div>

          {p.reengagementHistory?.length > 0 && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-wide text-white/40 mb-1">Recent activity</div>
              <ul className="text-xs text-white/70 space-y-1">
                {p.reengagementHistory.slice(-5).reverse().map((e, i) => (
                  <li key={i}>
                    <span className="text-white/50">{new Date(e.at).toLocaleString()}</span>{' '}
                    — {e.action} {e.detail && <span className="text-white/40">({e.detail})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function PersistencyDashboard() {
  const [minRisk, setMinRisk] = useState(70);
  const [openId, setOpenId] = useState(null);
  const qc = useQueryClient();

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['persistency-list', minRisk],
    queryFn: () => apiGet(`/persistency/policies?minRisk=${minRisk}&limit=100`).then((r) => r.data),
  });

  const actionMut = useMutation({
    mutationFn: ({ id, action }) => apiPost(`/persistency/policies/${id}/action`, { action }),
    onSuccess: (_r, vars) => {
      const verb = {
        approve_sms: 'SMS queued',
        approve_callback: 'Callback scheduled',
        snooze_7d: 'Snoozed for 7 days',
        dismiss: 'Dismissed',
      }[vars.action] || 'Done';
      toast.success(verb);
      qc.invalidateQueries({ queryKey: ['persistency-list'] });
      qc.invalidateQueries({ queryKey: ['persistency-policy', vars.id] });
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Action failed'),
  });

  const items = data?.items || [];

  return (
    <div className="min-h-screen">
      <TopBar />
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-display font-bold">Persistency AI</h1>
            <p className="text-sm text-white/60">Policies at risk of lapse — sorted by predicted risk score.</p>
          </div>
          <button onClick={() => refetch()} className="btn-secondary inline-flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        <div className="panel p-4 mb-4 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-white/70">Min risk:</label>
            <select
              value={minRisk}
              onChange={(e) => setMinRisk(Number(e.target.value))}
              className="bg-civix-panel border border-white/10 rounded-sm px-2 py-1 text-sm"
            >
              <option value={0}>All</option>
              <option value={50}>50+ (watch)</option>
              <option value={70}>70+ (high)</option>
              <option value={85}>85+ (critical)</option>
            </select>
          </div>
          <div className="text-sm text-white/70">
            <span className="text-emerald-400 font-bold">{data?.savedThisMonth ?? 0}</span> policies saved this month
          </div>
        </div>

        {isLoading && <div className="text-white/60 text-sm">Loading policies…</div>}
        {!isLoading && items.length === 0 && (
          <div className="panel p-8 text-center text-white/60">
            No policies meet that risk threshold. Lower the filter or wait for tonight's recompute.
          </div>
        )}

        <div className="space-y-3">
          {items.map((p) => (
            <PolicyRow key={p._id} p={p} onOpen={setOpenId} />
          ))}
        </div>
      </div>

      <DetailPanel
        policyId={openId}
        onClose={() => setOpenId(null)}
        onAction={(id, action) => actionMut.mutate({ id, action })}
      />
    </div>
  );
}
